import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { Box, Breadcrumbs, Link, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { AdminPageChromeState } from '../../context/AdminPageChromeContext';

/** Compact page title/breadcrumbs rendered inside the sticky Layout top bar. */
export function AdminTopBarChrome({ chrome }: { chrome: AdminPageChromeState | null }): ReactNode {
  const theme = useTheme();
  if (!chrome) return null;

  const { breadcrumbs, title, subtitle, startAdornment, endAdornment } = chrome;
  const hasLead = Boolean(
    (breadcrumbs && breadcrumbs.length > 0) || title != null || subtitle != null,
  );
  if (!hasLead && !startAdornment && !endAdornment) return null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ flex: 1, minWidth: 0, pr: 1 }}
      useFlexGap
      flexWrap="nowrap"
    >
      {startAdornment ? (
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{startAdornment}</Box>
      ) : null}
      {hasLead ? (
        <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
          {breadcrumbs && breadcrumbs.length > 0 ? (
            <Breadcrumbs
              aria-label="breadcrumb"
              sx={{
                mb: title || subtitle ? 0.15 : 0,
                '& .MuiBreadcrumbs-separator': { mx: 0.5 },
                '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
              }}
            >
              {breadcrumbs.map((crumb, i) => {
                const last = i === breadcrumbs.length - 1;
                if (crumb.to && !last) {
                  return (
                    <Link
                      key={`${crumb.label}-${i}`}
                      component={RouterLink}
                      to={crumb.to}
                      underline="hover"
                      color="inherit"
                      variant="caption"
                      sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                      {crumb.label}
                    </Link>
                  );
                }
                return (
                  <Typography
                    key={`${crumb.label}-${i}`}
                    variant="caption"
                    color={last ? 'text.primary' : 'text.secondary'}
                    sx={{ fontWeight: last ? 700 : 500, whiteSpace: 'nowrap' }}
                  >
                    {crumb.label}
                  </Typography>
                );
              })}
            </Breadcrumbs>
          ) : null}
          {title != null ? (
            typeof title === 'string' || typeof title === 'number' ? (
              <Typography
                variant="subtitle1"
                noWrap
                sx={{
                  fontWeight: 800,
                  letterSpacing: -0.02,
                  lineHeight: 1.25,
                  background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {title}
              </Typography>
            ) : (
              title
            )
          ) : null}
          {subtitle != null ? (
            typeof subtitle === 'string' || typeof subtitle === 'number' ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: 'block', lineHeight: 1.35, maxWidth: 560 }}
              >
                {subtitle}
              </Typography>
            ) : (
              subtitle
            )
          ) : null}
        </Box>
      ) : (
        <Box sx={{ flex: 1 }} />
      )}
      {endAdornment ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          sx={{ flexShrink: 0, justifyContent: 'flex-end' }}
        >
          {endAdornment}
        </Stack>
      ) : null}
    </Stack>
  );
}
